import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"

export type FormRequiredSlotProps = Omit<
  ComponentProps<"input">,
  "type" | "onChange" | "defaultValue" | "children" | "tabIndex" | "aria-hidden"
> & {
  // The current value of the wrapping widget. Treat empty / nullish as
  // "not selected" so the browser's required-field validation triggers.
  value: string | number | null | undefined
}

// Visually-hidden text input that lets non-input widgets (TenantPicker, our
// `Select`, custom pickers) participate in the generic `Form` validation.
// Render it next to the widget; when `required` is set, the browser tracks
// emptiness via the input and `Form` blocks submission + reports the error
// just like for a regular `<Input required>`.
//
// `aria-hidden` + `tabIndex={-1}` keep it out of the keyboard tab order and
// the accessibility tree — the visible widget already has its own a11y
// labels, so screen readers shouldn't see this shadow.
function FormRequiredSlot(props: FormRequiredSlotProps) {
  const { value, className, name, ...inputProps } = props

  const stringValue = value === null || value === undefined ? "" : String(value)

  return (
    <input
      type="text"
      aria-hidden
      tabIndex={-1}
      name={name}
      value={stringValue}
      onChange={() => {
        // Controlled; widget owns the value.
      }}
      className={cn("form-required-slot", className)}
      {...inputProps}
    />
  )
}

export default FormRequiredSlot
